import { useReducer, useEffect, useMemo } from 'react';

import { useGoogleAnalyticsErrorContext } from '@hydra/common/context';
import { useSyncThirdParty } from '@hydra/common/customHooks';
import * as fetcher from '@hydra/utils/fetcher';

interface GoogleAnalyticsAuth {
  url: string
  authenticated: boolean
  invalidGrant: boolean
  viewId?: string
  accessToken?: string
  loading?: boolean
}

type GoogleAnalyticsAuthAction = { type: 'UPDATE_GA', authenticated: boolean, invalidGrant: boolean }
  | { type: 'UPDATE_TOKEN', viewId: string, accessToken: string }

function googleAnalyticsReducer (state: GoogleAnalyticsAuth, action: GoogleAnalyticsAuthAction) {
  switch (action.type) {
    case 'UPDATE_GA':
      return {
        ...state,
        loading: true,
        authenticated: action.authenticated,
        invalidGrant: action.invalidGrant
      };
    case 'UPDATE_TOKEN':
      return {
        ...state,
        loading: false,
        viewId: action.viewId,
        accessToken: action.accessToken
      };
    default:
      return state;
  }
}

function init (googleAnalytcisData: GoogleAnalyticsAuth) {
  return {
    ...googleAnalytcisData,
    loading: googleAnalytcisData.authenticated && !googleAnalytcisData.viewId && !googleAnalytcisData.accessToken
  };
}

function useGoogleAnalyticsAuth (props: GoogleAnalyticsAuth) {
  const [{
    url,
    authenticated,
    invalidGrant,
    viewId,
    accessToken,
    loading
  }, dispatch] = useReducer(googleAnalyticsReducer, props, init);
  const { setGaError } = useGoogleAnalyticsErrorContext();

  useEffect(() => {
    const fetchGaToken = async () => {
      const projectUrl = `https://${url}`;
      const urlToAnalyze = new URL(projectUrl).host.replace(/^www\./, '');
      const endpoint = '/api/third-party/token?url=' + encodeURIComponent(urlToAnalyze);
      try {
        const res = await fetcher.get(endpoint);
        if (res.status === 200) {
          const jsonRes = await res.json();
          dispatch({
            type: 'UPDATE_TOKEN',
            ...jsonRes
          });
        } else {
          setGaError(res.status);
        }
      } catch (err) {
        setGaError(err.code);
      }
    };
    if (authenticated && !viewId && !accessToken) {
      fetchGaToken();
    }
  }, [authenticated, viewId, accessToken, url, setGaError]);

  const { handleSyncAccount } = useSyncThirdParty({
    thirdPartyAccount: 'GAData',
    projectUrl: url,
    updateData: data => {
      dispatch({
        type: 'UPDATE_GA',
        authenticated: data.authenticated,
        invalidGrant: data.invalidGrant
      });
    }
  });

  const views = useMemo(() => ({
    query: {
      ids: 'ga:' + viewId
    }
  }), [viewId]);

  return {
    handleSyncAccount,
    authenticated,
    invalidGrant,
    accessToken,
    viewId,
    views,
    loading
  };
}

export default useGoogleAnalyticsAuth;
